/**
 * HNS admin Results audit log.
 *
 * Lists the timeline the server built for each round in the admin Results
 * snapshot — round start, seekers released, captures, disqualifications,
 * Marco Polo calls and answers, rule changes, Krall's Accord, round end.
 * Disabled rounds are left out.
 *
 * Newest first, like the FPR log.
 */

import { useMemo } from 'react';
import type {
  AdminHnsResultsEvent,
  AdminHnsResultsEventType,
  AdminHnsResultsResponse,
} from '@swtorbit/shared-socket-events';
import { AuditLogShell } from '../results';

interface HnsAuditEvent extends AdminHnsResultsEvent {
  roundNumber: number;
}

const TYPE_ICON: Record<AdminHnsResultsEventType, string> = {
  round_started: '▶️',
  seekers_released: '⏱️',
  player_captured: '🎯',
  player_disqualified: '🚫',
  marco_polo: '📣',
  marco_polo_response: '🙋',
  ruleset_change: '📜',
  kralls_accord: '🛡️',
  round_ended: '🏁',
};

function fmtUtc(ms: number): string {
  return new Date(ms).toISOString().replace('T', ' ').slice(0, 19);
}

interface Props {
  /** Hide and Seek's results, or Hide and Seek Teams' (rounds shaped the same). */
  results: { rounds: Array<Pick<AdminHnsResultsResponse['rounds'][number], 'roundNumber' | 'disabled' | 'events'>> };
}

export function HnsResultsAuditLog({ results }: Props) {
  const events = useMemo<HnsAuditEvent[]>(() => {
    const out: HnsAuditEvent[] = [];
    for (const round of results.rounds) {
      if (round.disabled) continue;
      for (const event of round.events) {
        out.push({ ...event, roundNumber: round.roundNumber });
      }
    }
    out.sort((a, b) => b.utcMs - a.utcMs);
    return out;
  }, [results]);

  return (
    <AuditLogShell eventCount={events.length}>
      {events.map((e, idx) => (
        <div
          key={`${e.utcMs}-${e.type}-${idx}`}
          className="d-flex gap-xs align-items-center"
        >
          <span className="text-slate" style={{ minWidth: '11.5em' }}>{fmtUtc(e.utcMs)}</span>
          <span className="badge badge--pill ff-display">R{e.roundNumber}</span>
          <span>{TYPE_ICON[e.type]}</span>
          <span className="text-white flex-1">{e.label}</span>
          {e.detail && <span className="text-cyan">{e.detail}</span>}
        </div>
      ))}
    </AuditLogShell>
  );
}
